<template>
  <div>
    <vx-card class="mb-4">
      <h4 class="mb-1">{{ $t('influencer.data') }}</h4>
      <small class="text-muted d-block mb-3">{{ $t('influencer.data_hint') }}</small>

      <!--
        Cadastrar não aprova: o influencer nasce pendente e só participa de
        campanha, gera link e recebe comissão depois de aprovado. Avisar aqui
        evita o cadastro ser dado como concluído e o perfil ficar parado na fila.
      -->
      <b-alert v-if="!isEdit" variant="warning" show class="mb-3 p-1">
        <small>{{ $t('influencer.pending_on_create') }}</small>
      </b-alert>

      <validation-observer ref="influencerForm" tag="div">
        <div class="vx-row">
          <div class="vx-col sm:w-5/12 w-full">
            <b-form-group :label="$t('influencer.name')" label-for="name">
              <b-form-input id="name" v-model="model.name" type="text" class="required" :disabled="isEdit"/>
              <small v-if="isEdit" class="text-muted">{{ $t('influencer.name_locked') }}</small>
            </b-form-group>
          </div>
          <div class="vx-col sm:w-4/12 w-full">
            <b-form-group :label="$t('influencer.email')" label-for="email">
              <b-form-input id="email" v-model="model.email" type="email" class="required" :disabled="isEdit"/>
            </b-form-group>
          </div>
          <div class="vx-col sm:w-3/12 w-full">
            <b-form-group :label="$t('influencer.person_type')" label-for="person_type">
              <b-form-radio-group
                id="person_type"
                v-model="model.person_type"
                :options="personTypeList"
                @change="onPersonTypeChange"/>
            </b-form-group>
          </div>
        </div>

        <div class="vx-row">
          <div class="vx-col w-full" :class="identityColClass">
            <b-form-group :label="documentLabel" label-for="document">
              <validation-provider
                #default="{ errors }"
                :name="documentLabel"
                :rules="documentRules">
                <b-form-input
                  id="document"
                  v-model="model.document"
                  type="text"
                  :state="errors.length > 0 ? false : null"
                  v-mask="documentMask"/>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </div>
          <!--
            Cadastrar influencer cria User + People, então a idade mínima da LGPD
            (Art. 14) vale aqui como em qualquer outro cadastro. Só em criação: a
            edição mexe apenas nos campos do influencer, e o nascimento mora em
            `ppl.people` — mandá-lo no update não gravaria nada.
          -->
          <div v-if="!isEdit" class="vx-col sm:w-3/12 w-full">
            <validation-provider
              #default="{ errors }"
              :name="$t('influencer.birthdate')"
              rules="required|min_age"
              slim>
              <b-form-group :label="$t('influencer.birthdate')" label-for="birthdate">
                <b-form-input
                  id="birthdate"
                  v-model="model.birthdate"
                  type="date"
                  class="required"/>
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </div>
          <div class="vx-col w-full" :class="identityColClass">
            <b-form-group :label="$t('influencer.slug')" label-for="slug">
              <b-form-input id="slug" v-model="model.slug" type="text"/>
              <small class="text-muted">{{ $t('influencer.slug_hint') }}</small>
            </b-form-group>
          </div>
          <div class="vx-col w-full" :class="identityColClass">
            <b-form-group :label="$t('influencer.level')" label-for="level_id">
              <v-select
                id="level_id"
                v-model="model.level_id"
                :options="levels"
                :reduce="opt => opt.id"
                label="name"
                class="w-full">
                <div slot="no-options">{{ $t('influencer.no_levels') }}</div>
              </v-select>
            </b-form-group>
          </div>
        </div>

        <div class="vx-row">
          <div class="vx-col sm:w-8/12 w-full">
            <b-form-group :label="$t('influencer.bio')" label-for="bio">
              <b-form-textarea id="bio" v-model="model.bio" rows="2"/>
            </b-form-group>
          </div>
          <div class="vx-col sm:w-4/12 w-full">
            <b-form-group :label="$t('influencer.groups')" label-for="groups">
              <v-select
                id="groups"
                v-model="model.group_ids"
                :options="groups"
                :reduce="opt => opt.id"
                label="name"
                multiple
                class="w-full">
                <div slot="no-options">{{ $t('influencer.no_groups') }}</div>
              </v-select>
            </b-form-group>
          </div>
        </div>

        <b-form-group :label="$t('influencer.notes_internal')" label-for="notes_internal">
          <b-form-textarea id="notes_internal" v-model="model.notes_internal" rows="2"/>
          <small class="text-muted">{{ $t('influencer.notes_internal_hint') }}</small>
        </b-form-group>
      </validation-observer>
    </vx-card>

    <vx-card class="mb-4">
      <div class="flex justify-between items-center mb-1">
        <h4 class="mb-0">{{ $t('influencer.networks') }}</h4>
        <b-button size="sm" variant="outline-primary" @click="addProfile">
          <feather-icon icon="PlusIcon" class="mr-25"/>{{ $t('AddNew') }}
        </b-button>
      </div>
      <small class="text-muted d-block mb-3">{{ $t('influencer.networks_hint') }}</small>

      <div v-if="!model.social_profiles.length" class="text-muted mb-2">{{ $t('influencer.no_networks') }}</div>

      <div v-for="(profile, index) in model.social_profiles" :key="index" class="vx-row items-end mb-1">
        <div class="vx-col sm:w-2/12 w-full">
          <b-form-group :label="$t('influencer.network')">
            <b-form-select v-model="profile.network" :options="networkList"/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-3/12 w-full">
          <b-form-group :label="$t('influencer.handle')">
            <b-form-input v-model="profile.handle" type="text" placeholder="@perfil"/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-2/12 w-full">
          <b-form-group :label="$t('influencer.followers')">
            <b-form-input v-model="profile.followers" type="number" min="0"/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-2/12 w-full">
          <b-form-group :label="$t('influencer.engagement_rate')">
            <b-form-input v-model="profile.engagement_rate" type="number" step="0.01" min="0"/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-2/12 w-full">
          <b-form-group :label="$t('influencer.verified')">
            <b-form-checkbox v-model="profile.verified" switch/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-1/12 w-full mb-1">
          <feather-icon
            icon="Trash2Icon"
            class="h-5 w-5 stroke-current text-danger cursor-pointer"
            @click="removeProfile(index)"/>
        </div>
      </div>
    </vx-card>

    <vx-card v-if="isEdit" class="mb-4">
      <h4 class="mb-1">{{ $t('influencer.situation') }}</h4>
      <div class="flex items-center gap-2 mb-2">
        <vs-chip transparent :color="statusColor" class="text-base">{{ statusLabel }}</vs-chip>
        <span v-if="model.rejected_reason" class="text-muted small">{{ model.rejected_reason }}</span>
      </div>
      <div class="flex flex-row gap-2 flex-wrap">
        <b-button v-permission="'influencers.approve'" v-if="model.status !== 'AP'" size="sm" variant="success" @click="doAction('approve')">
          {{ $t('influencer.approve') }}
        </b-button>
        <b-button v-permission="'influencers.approve'" v-if="model.status === 'AP'" size="sm" variant="outline-warning" @click="doAction('suspend')">
          {{ $t('influencer.suspend') }}
        </b-button>
        <b-button v-permission="'influencers.approve'" v-if="model.status === 'SU'" size="sm" variant="outline-success" @click="doAction('reactivate')">
          {{ $t('influencer.reactivate') }}
        </b-button>
        <b-button v-permission="'influencers.edit'" size="sm" variant="outline-primary" @click="doAction('refreshLevel')">
          {{ $t('influencer.refresh_level') }}
        </b-button>
      </div>
    </vx-card>

    <b-form-row class="justify-end mt-2">
      <b-col cols="auto">
        <b-button variant="primary" @click="createOrUpdate">{{ $t('save') }}</b-button>
      </b-col>
      <b-col cols="auto">
        <b-button variant="outline-secondary" @click="goBack">
          <feather-icon icon="CornerUpLeftIcon" class="mr-50"/>
          <span class="align-middle">{{ $t('back') }}</span>
        </b-button>
      </b-col>
    </b-form-row>
  </div>
</template>

<script>
import {
  BFormGroup, BFormInput, BFormTextarea, BFormSelect, BFormRadioGroup,
  BFormCheckbox, BButton, BFormRow, BCol, BAlert
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
// Importar @validations pelo efeito colateral: é esse módulo que registra as
// regras globais do vee-validate usadas aqui — `cpf` e `cnpj`, que conferem o
// dígito verificador e ignoram a pontuação da máscara.
import '@validations'
import InfluencerService from '@/services/api/InfluencerService'
import InfluencerLevelService from '@/services/api/InfluencerLevelService'
import InfluencerGroupService from '@/services/api/InfluencerGroupService'

const DOCUMENT_MASK = {
  PF: '###.###.###-##',
  PJ: '##.###.###/####-##'
}

export default {
  components: {
    BFormGroup, BFormInput, BFormTextarea, BFormSelect, BFormRadioGroup,
    BFormCheckbox, BButton, BFormRow, BCol, BAlert, vSelect,
    ValidationProvider, ValidationObserver
  },
  props: {
    id: { type: [Number, String], default: null }
  },
  data: () => ({
    service: null,
    levelService: null,
    groupService: null,
    levels: [],
    groups: [],
    model: {
      id: null,
      name: '',
      email: '',
      person_type: 'PF',
      document: '',
      birthdate: '',
      slug: '',
      bio: '',
      notes_internal: '',
      level_id: null,
      group_ids: [],
      social_profiles: [],
      status: 'PE',
      rejected_reason: null
    },
    personTypeList: [
      { text: 'Pessoa física', value: 'PF' },
      { text: 'Pessoa jurídica', value: 'PJ' }
    ],
    networkList: [
      { text: 'Instagram', value: 'IG' },
      { text: 'TikTok', value: 'TT' },
      { text: 'YouTube', value: 'YT' },
      { text: 'Facebook', value: 'FB' },
      { text: 'X (Twitter)', value: 'TW' },
      { text: 'Kwai', value: 'KW' },
      { text: 'Outra', value: 'OT' }
    ]
  }),
  computed: {
    isEdit() {
      return !_.isNil(this.id)
    },
    isLegalEntity() {
      return this.model.person_type === 'PJ'
    },
    /** A linha de identificação perde a data de nascimento na edição — sem isso sobraria um vão. */
    identityColClass() {
      return this.isEdit ? 'sm:w-4/12' : 'sm:w-3/12'
    },
    documentLabel() {
      return this.isLegalEntity ? this.$t('influencer.document_cnpj') : this.$t('influencer.document_cpf')
    },
    documentMask() {
      return this.isLegalEntity ? DOCUMENT_MASK.PJ : DOCUMENT_MASK.PF
    },
    /**
     * Só uma das duas regras vale por vez — quem escolhe é o tipo de pessoa.
     * Sem `required` de propósito: o documento continua opcional (o backend o
     * aceita nulo); a regra só entra em ação quando o campo tem conteúdo,
     * porque o vee-validate pula validação de campo vazio sem `required`.
     */
    documentRules() {
      return this.isLegalEntity ? 'cnpj' : 'cpf'
    },
    statusLabel() {
      return this.$t(`influencer.status_.${this.model.status}`)
    },
    statusColor() {
      switch (this.model.status) {
        case 'AP': return 'success'
        case 'PE': return 'warning'
        case 'SU': return 'warning'
        case 'RJ': return 'danger'
        default: return 'dark'
      }
    }
  },
  methods: {
    /**
     * Trocar de tipo de pessoa limpa o documento: as máscaras têm tamanhos
     * diferentes, então o valor antigo seria remascarado em algo truncado — um
     * CNPJ virando um "CPF" de 11 dígitos parece preenchido e válido, mas não é.
     * Escuta o `change` do rádio, e não um watcher, porque o watcher também
     * dispararia ao carregar um cadastro existente e apagaria o valor salvo.
     */
    onPersonTypeChange() {
      this.model.document = ''
    },
    /**
     * O documento é gravado só com os dígitos, como em `people.document` e nos
     * cadastros de profissional e de pessoa jurídica. A pontuação da máscara é
     * de exibição — os gateways descartam ela de qualquer forma.
     */
    documentDigits() {
      return (this.model.document || '').replace(/\D/g, '') || null
    },
    addProfile() {
      this.model.social_profiles.push({ network: 'IG', handle: '', followers: null, engagement_rate: null, verified: false })
    },
    removeProfile(index) {
      this.model.social_profiles.splice(index, 1)
    },
    loadAuxData() {
      this.levelService.all().then(list => { this.levels = list || [] }, () => {})
      this.groupService.all().then(list => { this.groups = list || [] }, () => {})
    },
    loadData(id) {
      this.$vs.loading()
      this.service.read(id).then(
        response => {
          this.model.id = response.id
          this.model.name = response.name
          this.model.email = response.email
          this.model.person_type = response.person_type
          this.model.document = response.document
          this.model.slug = response.slug
          this.model.bio = response.bio
          this.model.notes_internal = response.notes_internal
          this.model.level_id = response.level_id
          this.model.status = response.status
          this.model.rejected_reason = response.rejected_reason
          this.model.group_ids = (response.groups || []).map(g => g.id)
          this.model.social_profiles = (response.social_profiles || []).map(p => ({
            network: p.network,
            handle: p.handle,
            followers: p.followers,
            engagement_rate: p.engagement_rate,
            verified: !!p.verified_at
          }))
        },
        () => { this.$vs.loading.close() }
      ).finally(() => { this.$vs.loading.close() })
    },
    /**
     * Criar e editar seguem caminhos diferentes de propósito: criar precisa
     * montar usuário e pessoa (endpoint `register`), enquanto editar mexe só
     * nos campos do influencer. Perfis e grupos têm endpoints próprios porque
     * são coleções, não campos.
     */
    createOrUpdate() {
      this.$refs.influencerForm.validate().then(valid => {
        if (valid) this.persist()
      })
    },
    persist() {
      this.$vs.loading()

      const done = data => {
        if (this.isEdit) {
          this.notifySuccess(this.$vs, this.$t('messages.success'))
          return
        }

        // Na criação a mensagem precisa dizer que o cadastro ficou pendente,
        // senão a tela de edição — para onde vamos em seguida — parece só o
        // cadastro salvo, e o botão de aprovar do card "Situação" passa batido.
        this.notifySuccess(this.$vs, this.$t('influencer.created_pending'), 5000)
        this.$router.push(`/influencers/${data.id}/edit`)
      }
      const fail = error => this.showErrors(this.$validator, error, this)

      if (!this.isEdit) {
        this.service.register({
          name: this.model.name,
          email: this.model.email,
          person_type: this.model.person_type,
          document: this.documentDigits(),
          birthdate: this.model.birthdate || null,
          slug: this.model.slug || null,
          bio: this.model.bio,
          notes_internal: this.model.notes_internal,
          level_id: this.model.level_id,
          group_ids: this.model.group_ids,
          social_profiles: this.model.social_profiles
        }).then(done, fail).finally(() => { this.$vs.loading.close() })
        return
      }

      this.service.createOrUpdate({
        id: this.id,
        person_type: this.model.person_type,
        document: this.documentDigits(),
        slug: this.model.slug,
        bio: this.model.bio,
        notes_internal: this.model.notes_internal,
        level_id: this.model.level_id
      }).then(
        data => Promise.all([
          this.service.saveSocialProfiles(this.id, this.model.social_profiles),
          this.service.saveGroups(this.id, this.model.group_ids)
        ]).then(() => done(data)),
        fail
      ).finally(() => { this.$vs.loading.close() })
    },
    doAction(action) {
      this.$vs.loading()
      const call = {
        approve: () => this.service.approve(this.id),
        suspend: () => this.service.suspend(this.id, ''),
        reactivate: () => this.service.reactivate(this.id),
        refreshLevel: () => this.service.refreshLevel(this.id)
      }[action]

      call().then(
        () => {
          this.notifySuccess(this.$vs, this.$t('messages.success'))
          this.loadData(this.id)
        },
        error => this.showErrors(this.$validator, error, this)
      ).finally(() => { this.$vs.loading.close() })
    },
    goBack() {
      this.$router.push('/influencers')
    }
  },
  beforeMount() {
    this.service = InfluencerService.build(this.$vs)
    this.levelService = InfluencerLevelService.build(this.$vs)
    this.groupService = InfluencerGroupService.build(this.$vs)
  },
  mounted() {
    this.loadAuxData()
    if (this.id) {
      this.loadData(this.id)
    }
  }
}
</script>
